import { autoUpdate, computePosition, flip, Placement, shift } from '@floating-ui/react-dom-interactions'
import cx from 'classnames'
import { KeyboardShortcutTagProps } from 'common/KeyboardShortcutTag/KeyboardShortcutTag'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { EditorView } from 'prosemirror-view'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useKeyPress } from 'react-use'
import Styles from './ProseDropdownPrompt.module.scss'
import { ButtonProps } from 'components/Button/Types'

export type ProseDropdownPromptProps = {
  view: EditorView
  anchorChildren: React.ReactNode
  anchorClassName?: string
  isDisabled?: boolean
  label: string
  shortcutKeys?: KeyboardShortcutTagProps['keys']
  shortcutModifiers?: KeyboardShortcutTagProps['modifiers']
  promptContainerClassName?: string
  dropdownComponent: (close: () => void) => React.ReactNode
  withChevron?: boolean
  active?: boolean
  forceOpen?: boolean
  onOpenChange?: (open: boolean) => void
  placement?: Placement
  size?: ButtonProps['size']
  yOffset?: number
}

export const ProseDropdownPrompt: React.FC<React.PropsWithChildren<ProseDropdownPromptProps>> = (props) => {
  const {
    view,
    anchorChildren,
    isDisabled = false,
    label,
    shortcutKeys,
    shortcutModifiers,
    dropdownComponent,
    promptContainerClassName,
    forceOpen = false,
    withChevron = false,
    active,
    anchorClassName,
    onOpenChange,
    placement = 'bottom',
    size = 'lg',
    yOffset = 0,
  } = props
  const [open, setOpen] = useState(false)
  const [chevron, setChevron] = useState<'up' | 'down' | undefined>(withChevron ? 'down' : undefined)
  const floatingRoot = document.getElementById('portal-root')
  const floatingRef = useRef<HTMLDivElement>(null)
  const refElement = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    onOpenChange?.(false)
    setOpen(false)
    if (
      !view.hasFocus() &&
      !document.activeElement?.classList.contains('ProseMirror') &&
      document.activeElement?.tagName.toLowerCase() !== 'input'
    ) {
      view.focus()
    }
  }, [view, setOpen, onOpenChange])
  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      const next = !prev
      if (!next) {
        view.focus()
      }
      onOpenChange?.(next)
      return next
    })
  }, [setOpen, view, onOpenChange])

  const positionAnchor = useMemo(
    () => () => {
      const refRect = refElement.current?.getBoundingClientRect()
      const anchor = anchorRef.current
      if (!refRect || !anchor) return
      Object.assign(anchor.style, {
        transform: `translate(${refRect.x}px, ${refRect.y}px)`,
      })
    },
    [refElement, anchorRef],
  )

  const [escapePressed] = useKeyPress('Escape')
  useEffect(() => {
    if (open) {
      const closeIfOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        const floatingEl = floatingRef.current
        const activationEl = refElement.current
        const clickableElementParents = [
          floatingEl,
          activationEl,
          document.getElementById('floating-ui-root'),
          document.getElementById('portal-root'),
        ]
        if (!floatingEl || !activationEl) return
        if (!clickableElementParents.some((el) => el?.contains(target))) {
          close()
        }
      }
      document.addEventListener('click', closeIfOutside, { capture: true })
      return () => document.removeEventListener('click', closeIfOutside)
    }
  }, [open, close])

  useEffect(() => {
    if (escapePressed) close()
  }, [escapePressed, close])

  useEffect(() => {
    if (forceOpen) setOpen(true)
  }, [forceOpen, setOpen])

  useEffect(() => {
    if (withChevron) {
      setChevron(open ? 'up' : 'down')
    }
  }, [open, withChevron])

  useLayoutEffect(() => {
    positionAnchor()
  })

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    const floatingEl = floatingRef.current
    if (!anchor || !floatingEl) return
    autoUpdate(anchor, floatingEl, () => {
      computePosition(anchor, floatingEl, {
        placement,
        middleware: [flip(), shift()],
      }).then(({ x, y, middlewareData }) => {
        const flipped = middlewareData?.flip?.index === 1
        const alignItems = flipped ? 'self-end' : 'self-start'
        const yPos = flipped ? y - 5 : y + yOffset
        Object.assign(floatingEl.style, {
          transform: `translate(${x}px, ${yPos}px)`,
          alignItems,
        })
      })
    })
  })

  if (!floatingRoot) return null

  return (
    <>
      <div ref={refElement} className={Styles.buttonWrapper}>
        <InlineFormatButton
          size={size}
          label={label}
          data-testid={label}
          shortcutKeys={shortcutKeys}
          shortcutModifiers={shortcutModifiers}
          active={open || active}
          onPress={toggleOpen}
          isDisabled={isDisabled}
          className={anchorClassName}
          withChevron={chevron}
        >
          {anchorChildren}
        </InlineFormatButton>
      </div>
      {ReactDOM.createPortal(
        <>
          <div
            ref={anchorRef}
            style={{
              height: '32px',
              width: '32px',
              position: 'absolute',
              pointerEvents: 'none',
            }}
          ></div>
          {open && (
            <div
              className={cx(Styles.promptContainer, promptContainerClassName, 'proseDropdownPromptdropdownContainer')}
              ref={floatingRef}
            >
              {dropdownComponent(close)}
            </div>
          )}
        </>,
        floatingRoot,
      )}
    </>
  )
}
